import React, { useContext, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Col, Form, Row } from 'react-bootstrap'
import Imgix from 'react-imgix'
import {
  FeatherLoader,
  GoBackIcon,
  showJourneyMediaDescription,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import { useDebounce } from '../common'
import API from '../../utils/API'
import { JourneyMediasContext } from '../../utils/contexts'

const isAttached = (mediasList, id) =>
  (mediasList || []).some((media) => media?._id === id)

const SearchMedias = ({
  render = () => {},
  journeyType,
  addMediaHandler,
  placeholder = 'Enter Audio Title',
  inputOnly = false,
  experienceChrome = false,
}) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 1000)
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [searchData, setSearchData] = useState({ medias: [], modules: [] })
  const { showError } = useErrorService()
  const { showNotification } = useNotificationService()
  const { mediasList } = useContext(JourneyMediasContext)

  useEffect(() => {
    render(debouncedSearch !== '')
  }, [debouncedSearch, render])

  useEffect(() => {
    if (search) {
      setSearch(debouncedSearch)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    const getSearchData = async () => {
      if (debouncedSearch === '') {
        setSearchData({ medias: [], modules: [] })
        setIsSearching(false)
        return
      }
      setIsSearching(true)
      try {
        const { data } = await API.AdminAPI.userList.getJouneySearchMedia(
          debouncedSearch,
          true,
        )
        const filteredModules = (data?.modules || []).map((module) => ({
          ...module,
          type: 'module',
        }))
        setSearchData({
          medias: [...(data?.medias || [])],
          modules: filteredModules,
        })
      } catch (e) {
        showError(e)
      }
      setIsSearching(false)
    }
    getSearchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const availableModules = useMemo(
    () =>
      (searchData?.modules || []).filter(
        (module) => !isAttached(mediasList, module._id),
      ),
    [searchData, mediasList],
  )

  const availableMedias = useMemo(
    () =>
      (searchData?.medias || []).filter(
        (media) => !isAttached(mediasList, media._id),
      ),
    [searchData, mediasList],
  )

  const saveMediaHandler = (mediaInfo, type) => {
    setIsLoading(true)

    const sameAudios = mediasList.filter((media) => media._id === mediaInfo._id)

    if (sameAudios.length > 0) {
      setSearch('')
      setSearchData({ medias: [], modules: [] })
      setIsLoading(false)
      showNotification('Please provide unique audio.', 'danger')
      return
    }

    const selectedMedia = { ...mediaInfo, mediaOrModuleId: mediaInfo._id, type }
    addMediaHandler(selectedMedia)
    setSearch('')
    setSearchData({ medias: [], modules: [] })
    setIsLoading(false)
  }

  const experienceResults = (
    <>
      {isSearching && debouncedSearch !== '' && (
        <div className='d-flex justify-content-center align-items-center my-3'>
          <FeatherLoader />
        </div>
      )}
      {debouncedSearch !== '' &&
        !isSearching &&
        availableModules.length === 0 &&
        availableMedias.length === 0 && (
          <div className='experience-lib-empty'>
            {(searchData?.medias?.length || 0) +
              (searchData?.modules?.length || 0) >
            0
              ? 'All matching audio is already attached.'
              : 'No matching audio found.'}
          </div>
        )}
      {debouncedSearch !== '' &&
        !isSearching &&
        (availableModules.length > 0 || availableMedias.length > 0) && (
          <div className='experience-lib-search-list'>
            {availableModules.map((moduleInfo) => (
              <div className='experience-lib-module' key={moduleInfo._id}>
                <div className='experience-lib-mod-head'>
                  <div className='experience-lib-mod-toggle'>
                    <span className='copy'>
                      {moduleInfo?.subTitle ? (
                        <small>{moduleInfo.subTitle}</small>
                      ) : null}
                      <b>{moduleInfo?.title}</b>
                    </span>
                  </div>
                  <button
                    type='button'
                    className='experience-lib-track-add'
                    disabled={isLoading}
                    onClick={() => saveMediaHandler(moduleInfo, 'module')}>
                    Add module
                  </button>
                </div>
              </div>
            ))}
            {availableMedias.map((mediaInfo) => (
              <div className='experience-lib-item' key={mediaInfo._id}>
                <span className='copy'>
                  <b>{mediaInfo.title}</b>
                  <small>{showJourneyMediaDescription(mediaInfo)}</small>
                </span>
                <button
                  type='button'
                  className='experience-lib-track-add'
                  disabled={isLoading}
                  onClick={() => saveMediaHandler(mediaInfo, 'media')}>
                  Add
                </button>
              </div>
            ))}
          </div>
        )}
    </>
  )

  const legacyResults = (
    <>
      {isSearching && debouncedSearch !== '' && (
        <div className='d-flex justify-content-center align-items-center my-3'>
          <FeatherLoader />
        </div>
      )}
      {searchData?.medias?.length === 0 &&
        searchData?.modules?.length === 0 &&
        debouncedSearch !== '' &&
        !isSearching && (
          <div className='d-flex justify-content-center align-items-center my-3'>
            <p className='mb-0'>No media/module found.</p>
          </div>
        )}
      {(searchData?.medias?.length > 0 || searchData?.modules?.length > 0) &&
        debouncedSearch !== '' &&
        !isSearching && (
          <div className='mt-3'>
            {searchData?.modules?.map((moduleInfo) => (
              <div key={moduleInfo._id}>
                {renderModuleMediaList(
                  moduleInfo,
                  () => {
                    setSearch(moduleInfo.title)
                    saveMediaHandler(moduleInfo, 'module')
                  },
                  isLoading,
                )}
              </div>
            ))}
            {searchData?.medias?.map((mediaInfo) => (
              <div key={mediaInfo._id}>
                {renderModuleMediaList(
                  mediaInfo,
                  () => {
                    setSearch(mediaInfo.title)
                    saveMediaHandler(mediaInfo, 'media')
                  },
                  isLoading,
                )}
              </div>
            ))}
          </div>
        )}
    </>
  )

  if (inputOnly) {
    return (
      <>
        <div className='experience-lib-search-wrap'>
          <Form.Control
            type='text'
            placeholder={placeholder}
            className='experience-lib-search'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={isLoading}
            autoComplete='off'
          />
        </div>
        <div className='experience-lib-search-results'>
          {experienceChrome ? experienceResults : legacyResults}
        </div>
      </>
    )
  }

  return (
    <div>
      <Form.Control
        type='text'
        placeholder={placeholder}
        className='experience-lib-search font-open-sans font-14 font-semibold leading-6 letter-spacing-minus-02'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        disabled={isLoading}
      />
      {legacyResults}
    </div>
  )
}

const renderModuleMediaList = (media, addMediaHandler, isLoading) => {
  const source = process.env.REACT_APP_IMG_SOURCE

  return (
    <Row
      className={classNames('mb-lg-3 mb-4', {
        'pointer-event-none': isLoading,
      })}>
      <Col className='search-col-first align-self-center'>
        <GoBackIcon
          className='media-remove-icon add-medias'
          onClick={addMediaHandler}
        />
      </Col>
      <Col className='search-col-second'>
        {media && (
          <div className='d-flex align-items-center'>
            <div className='media-image'>
              <Imgix
                className='border-radius-5 border-black mr-3'
                imgixParams={{ crop: 'edges', fit: 'crop', w: 144, h: 144 }}
                src={`${source}/${media.imageFilename}`}
                height={48}
                width={48}
                alt='image'
              />
            </div>
            <div className='d-flex flex-column justify-content-center'>
              <span className='font-open-sans font-13 font-semibold leading-4-5 letter-spacing-minus-04'>
                {media.title}
              </span>
              <span className='font-open-sans font-10 font-semibold leading-3-5 tracking-normal'>
                {showJourneyMediaDescription(media)}
              </span>
            </div>
          </div>
        )}
      </Col>
    </Row>
  )
}

export default SearchMedias
